/**
 * Hook to fetch user's accumulated swap balance from the Swap program's UserInfo PDA
 * This balance represents SOL accumulated from successful dust swaps
 */

import { useQuery } from '@tanstack/react-query';
import { PublicKey } from '@solana/web3.js';

import { getSwapUserBalance, getSwapUserInfoPDA } from '@/lib/solana/anchorClient';
import config from '@/config';

interface SwapUserBalanceResult {
  /** Balance in lamports */
  balanceLamports: number | null;
  /** Balance in SOL */
  balanceSol: number | null;
  /** The PDA address */
  pdaAddress: string | null;
  /** Whether the feature is enabled */
  isEnabled: boolean;
}

/**
 * Check if swap PDA balance display is enabled
 */
function isSwapPdaBalanceEnabled(): boolean {
  return (config as { SHOW_SWAP_PDA_BALANCE?: boolean }).SHOW_SWAP_PDA_BALANCE === true;
}

/**
 * Hook to fetch user's accumulated swap balance
 * Only queries when the feature flag is enabled
 *
 * @param walletAddress - User's wallet address as string
 */
export function useSwapUserBalance(walletAddress: string | undefined): SwapUserBalanceResult & {
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const isEnabled = isSwapPdaBalanceEnabled();

  const query = useQuery({
    queryKey: ['swapUserBalance', walletAddress],
    queryFn: async (): Promise<{ balanceLamports: number | null; pdaAddress: string }> => {
      if (!walletAddress) {
        throw new Error('No wallet address provided');
      }

      const publicKey = new PublicKey(walletAddress);
      const [pda] = getSwapUserInfoPDA(publicKey);
      const balance = await getSwapUserBalance(publicKey);

      return {
        balanceLamports: balance,
        pdaAddress: pda.toString(),
      };
    },
    enabled: isEnabled && !!walletAddress,
    staleTime: 10000, // 10 seconds
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const balanceLamports = query.data?.balanceLamports ?? null;
  const balanceSol = balanceLamports !== null ? balanceLamports / 1e9 : null;

  return {
    balanceLamports,
    balanceSol,
    pdaAddress: query.data?.pdaAddress ?? null,
    isEnabled,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
